/**
 * A day's play session: the bench, the blends you have made, and the budget.
 *
 * The answer is always the *newest* slide on the bench, which is what every
 * blend appends. That means the gesture is only ever "pick two", and there is
 * never a separate "now choose which of your colours counts" step. Undo is
 * free and unlimited; the budget, not the search, is the constraint.
 */

import { Lab, deltaE, mixLab, oklabToHex } from '../core/colour';
import { Puzzle } from '../core/generator';
import { nameForLab } from '../core/palette';

export interface Slide {
  id: string;
  lab: Lab;
  hex: string;
  /** Pigment name, or null for anything the player made. */
  name: string | null;
  /** Ids of the two slides this came from. Null for bench pigments. */
  parents: [string, string] | null;
  /** How many blends deep. Pigments are 0. */
  depth: number;
}

interface Snapshot {
  slides: Slide[];
  blendsUsed: number;
}

export type SessionEvent = 'change' | 'blend' | 'undo' | 'reset' | 'submit';

export class Session {
  readonly puzzle: Puzzle;
  private slides: Slide[] = [];
  private selected: string[] = [];
  private history: Snapshot[] = [];
  private counter = 0;
  private listeners = new Set<(e: SessionEvent) => void>();
  private submitted = false;

  blendsUsed = 0;

  constructor(puzzle: Puzzle) {
    this.puzzle = puzzle;
    this.reset();
  }

  // -------------------------------------------------------------------------
  // Reads
  // -------------------------------------------------------------------------

  get bench(): readonly Slide[] {
    return this.slides;
  }

  get budget(): number {
    return this.puzzle.spec.blends;
  }

  get remaining(): number {
    return Math.max(0, this.budget - this.blendsUsed);
  }

  get selection(): readonly string[] {
    return this.selected;
  }

  get isSubmitted(): boolean {
    return this.submitted;
  }

  /** The player's current answer: the newest slide they made. */
  get answer(): Slide | null {
    if (this.blendsUsed === 0) return null;
    return this.slides[this.slides.length - 1] ?? null;
  }

  /** Live distance to the target, for the reveal and for debug. */
  get distance(): number {
    const a = this.answer;
    return a ? deltaE(a.lab, this.puzzle.target.lab) : Infinity;
  }

  get canBlend(): boolean {
    return !this.submitted && this.selected.length === 2 && this.remaining > 0;
  }

  get canUndo(): boolean {
    return !this.submitted && this.history.length > 0;
  }

  get canSubmit(): boolean {
    return !this.submitted && this.blendsUsed > 0;
  }

  isSelected(id: string): boolean {
    return this.selected.includes(id);
  }

  // -------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------

  /**
   * Tap a slide. First tap selects, second tap on a different slide blends.
   * Tapping the selected slide again deselects it.
   */
  tap(id: string): void {
    if (this.submitted) return;
    const slide = this.slides.find((s) => s.id === id);
    if (!slide) return;

    if (this.selected.includes(id)) {
      this.selected = this.selected.filter((s) => s !== id);
      this.emit('change');
      return;
    }

    if (this.selected.length === 2) {
      this.selected = [this.selected[1], id];
      this.emit('change');
      return;
    }

    this.selected.push(id);
    if (this.selected.length === 2) {
      if (this.remaining > 0) this.blend();
      else this.emit('change');
    } else {
      this.emit('change');
    }
  }

  /**
   * Mix the two selected slides into a new one. Tapping a second slide calls
   * this for you; it is public so a Blend button can drive it too.
   */
  blend(): void {
    if (!this.canBlend) return;
    const [aId, bId] = this.selected;
    const a = this.slides.find((s) => s.id === aId)!;
    const b = this.slides.find((s) => s.id === bId)!;

    this.history.push(this.snapshot());

    const lab = mixLab(a.lab, b.lab);
    const slide: Slide = {
      id: `m${this.counter++}`,
      lab,
      hex: oklabToHex(lab),
      name: nameForLab(lab),
      parents: [a.id, b.id],
      depth: Math.max(a.depth, b.depth) + 1,
    };

    this.slides.push(slide);
    this.selected = [];
    this.blendsUsed++;
    this.emit('blend');
  }

  /**
   * Undo the last blend. Selection is cleared rather than restored: landing
   * back on a live two-slide selection you cannot re-blend by tapping is
   * more confusing than simply starting the pick again.
   */
  undo(): void {
    if (this.submitted) return;
    const snap = this.history.pop();
    if (!snap) return;
    this.slides = snap.slides;
    this.blendsUsed = snap.blendsUsed;
    this.selected = [];
    this.emit('undo');
  }

  reset(): void {
    const bench = this.puzzle.bench;
    this.slides = bench.map((p, i) => ({
      id: `p${i}`,
      lab: p.lab,
      hex: p.hex,
      name: p.name,
      parents: null,
      depth: 0,
    }));
    this.selected = [];
    this.history = [];
    this.blendsUsed = 0;
    this.counter = 0;
    this.submitted = false;
    this.emit('reset');
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.submitted = true;
    this.emit('submit');
  }

  // -------------------------------------------------------------------------
  // Plumbing
  // -------------------------------------------------------------------------

  on(fn: (e: SessionEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: SessionEvent): void {
    for (const fn of this.listeners) fn(e);
  }

  private snapshot(): Snapshot {
    return {
      slides: this.slides.slice(),
      blendsUsed: this.blendsUsed,
    };
  }
}
